import { ChangeEvent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import consoleLog from "../../utils/consoleLog";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { GoogleApiWrapper } from "google-maps-react";

import useMediaQuery from "@material-ui/core/useMediaQuery";

import { selectCheckoutInfo } from "../../redux/checkout/checkout.selectors";
import { checkoutActions } from "../../redux/checkout/checkout.reducer";
import { createShippingAddress, getCustomer } from "../../apis/payment.apis";

import withAuth from "../../hocs/withAuth";

import useStyles from "./checkout-info.styles";

import CheckoutLinks from "../../components/checkout-links";
import CheckoutSummary from "../../components/checkout-summary";
import Loading from "../../components/loading";
import StripeIcon from "../../assets/icons/stripe-icon";
import TextInput from "../../components/text-input";
import SelectInput from "../../components/select-input";

const phoneRegex =
  /^(?:\+?1[-. ]?)?\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;

const validationSchema = yup.object().shape({
  contact: yup.object().shape({
    email: yup.string().required("Required").email("Invalid email"),
    keepMeUpToDate: yup.boolean(),
  }),
  shippingAddress: yup.object().shape({
    firstname: yup.string().optional(),
    lastname: yup
      .string()
      .required("Required")
      .min(2, "Too Short!")
      .max(50, "Too Long!"),
    company: yup.string(),
    line1: yup.string().required("Required"),
    line2: yup.string(),
    country: yup.string().required("Required"),
    state: yup.string().required("Required"),
    city: yup.string().required("Required"),
    postal_code: yup.string().required("Required"),
    phone: yup
      .string()
      .nullable()
      .transform((value, originalValue) =>
        originalValue.trim() === "" ? null : value,
      )
      .matches(phoneRegex, "Invalid Phone Number"),
  }),
});

const CheckoutInfoPage = (props: any) => {
  const { checkoutRoutes, google } = props;

  const history = useHistory();
  const dispatch = useDispatch();
  const matches = useMediaQuery("(min-width:1200px)");

  const checkoutInfo = useSelector(selectCheckoutInfo);

  const [currentCustomer, setCurentCustomer] = useState<any>(null);
  const [savedShippingAddress, setSavedShippingAddress] = useState<any[]>([]);
  const [selectedShippingAddress, setSelectedShippingAddress] = useState<{
    id: string;
    name: string;
    phone?: string;
    address: {
      city: string;
      country: string;
      line1: string;
      line2?: string;
      postal_code: string;
      state: string;
    };
  }>({
    id: checkoutInfo.shippingAddress.id || "",
    name: checkoutInfo.shippingAddress.firstname
      ? `${checkoutInfo.shippingAddress.firstname} ${checkoutInfo.shippingAddress.lastname}`
      : checkoutInfo.shippingAddress.lastname || "",
    phone: checkoutInfo.shippingAddress.phone || "",
    address: {
      city: checkoutInfo.shippingAddress.city || "",
      country: checkoutInfo.shippingAddress.country || "",
      line1: checkoutInfo.shippingAddress.line1 || "",
      line2: checkoutInfo.shippingAddress.line2 || "",
      postal_code: checkoutInfo.shippingAddress.postal_code || "",
      state: checkoutInfo.shippingAddress.state || "",
    },
  });
  const [isAddingNewShippingAddress, setIsAddingNewShippingAddress] =
    useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const classes = useStyles({ matches });

  useEffect(() => {
    setTimeout(() => {
      setIsLoading(false);
    }, 500);

    window.scrollTo({
      top: 0,
    });
  }, []);

  useEffect(() => {
    if (selectedShippingAddress?.name.split(" ").length === 1) {
      setValue("shippingAddress.firstname", "");
      setValue(
        "shippingAddress.lastname",
        selectedShippingAddress?.name.split(" ")[0],
      );
    } else if (selectedShippingAddress?.name.split(" ").length === 2) {
      setValue(
        "shippingAddress.firstname",
        selectedShippingAddress?.name.split(" ")[0],
      );
      setValue(
        "shippingAddress.lastname",
        selectedShippingAddress?.name.split(" ")[1],
      );
    } else {
      setValue("shippingAddress.firstname", "");
      setValue("shippingAddress.lastname", "");
    }

    setValue("shippingAddress.phone", selectedShippingAddress?.phone || "");
    setValue(
      "shippingAddress.city",
      selectedShippingAddress?.address.city || "",
    );
    setValue(
      "shippingAddress.country",
      selectedShippingAddress?.address.country || "",
    );
    setValue(
      "shippingAddress.line1",
      selectedShippingAddress?.address.line1 || "",
    );
    setValue(
      "shippingAddress.line2",
      selectedShippingAddress?.address.line2 || "",
    );
    setValue(
      "shippingAddress.postal_code",
      selectedShippingAddress?.address.postal_code || "",
    );
    setValue(
      "shippingAddress.state",
      selectedShippingAddress?.address.state || "",
    );
  }, [selectedShippingAddress]);

  const initialValues = checkoutInfo;

  const {
    register,
    watch,
    formState: { errors },
    getValues,
    setValue,
    handleSubmit,
  } = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
  });

  const values = getValues();

  watch();

  // get Customer from server
  useEffect(() => {
    setIsLoading(true);
    getCustomer({ customerId: "cus_JBG5WPOsANVaCS" })
      .then((res) => {
        setCurentCustomer(res);
      })
      .catch((err) => {
        consoleLog(err);
        return null;
      });
  }, []);

  // set savedShippingAddress
  useEffect(() => {
    let shippingAddressArr: any[] = [];
    if (!currentCustomer) {
      return;
    }
    const { metadata } = currentCustomer;
    Object.keys(metadata)
      .filter((key) => key !== "payment_method_default")
      .reverse()
      .forEach((key) => {
        shippingAddressArr.push({
          id: key,
          ...JSON.parse(metadata[key]),
        });
      });
    setSavedShippingAddress(shippingAddressArr);
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  }, [currentCustomer]);

  // whenever saved shipping address is set, set shipping address as the latest shipping address
  useEffect(() => {
    if (savedShippingAddress.length !== 0) {
      let address = savedShippingAddress.reduce((a, b) => {
        const latestDate = Math.max(
          parseInt(a.id?.split("_")[1], 10),
          parseInt(b.id?.split("_")[1], 10),
        );
        return savedShippingAddress.find(
          (sm) => sm.id === `SHIPPING_${latestDate}`,
        );
      });
      if (checkoutInfo.shippingAddress.id) {
        address = savedShippingAddress.find(
          (sm) => sm.id === checkoutInfo.shippingAddress.id,
        );
      }
      setSelectedShippingAddress(address);
      setIsAddingNewShippingAddress(false);
    } else {
      setIsAddingNewShippingAddress(true);
    }
  }, [savedShippingAddress, checkoutInfo]);

  // handle select change event (selecting address)
  const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.currentTarget;
    switch (name) {
      case "useSavedAddressOrAddNewAddress":
        if (value === "addNewShippingAddress") {
          setIsAddingNewShippingAddress(true);
        } else {
          setIsAddingNewShippingAddress(false);
        }
        setSelectedShippingAddress(
          savedShippingAddress.find((sa) => sa.id === value),
        );
        break;
      default:
        break;
    }
  };

  const sleep = (milliseconds = 1000) =>
    new Promise((resolve) => setTimeout(resolve, milliseconds));

  // submit checkout info to Redux store
  const submitCheckoutInfo = (inputValues: any) =>
    dispatch({
      type: checkoutActions.submitInfo.type,
      payload: inputValues,
    });

  // add a new shipping address to Stripe
  const addNewShippingAddress = async (inputValues: any) => {
    const { shippingAddress } = inputValues;
    const {
      city,
      country,
      line1,
      line2,
      postal_code,
      state,
      firstname,
      lastname,
      phone,
    } = shippingAddress;

    let newShippingAddressId;
    if (currentCustomer) {
      newShippingAddressId = await createShippingAddress({
        customerId: currentCustomer.id,
        shippingAddress: {
          city,
          country,
          line1,
          line2,
          postal_code,
          state,
        },
        shippingName: firstname ? `${firstname} ${lastname}` : `${lastname}`,
        shippingPhone: phone,
      })
        .then((res) => {
          consoleLog(res);
          return Object.keys(res.body.metadata)
            .filter((key) => key !== "payment_method_default")
            .reduce((a, b) => {
              const latestDate = Math.max(
                parseInt(a?.split("_")[1], 10),
                parseInt(b?.split("_")[1], 10),
              );
              return `SHIPPING_${latestDate}`;
            });
        })
        .catch((err) => {
          consoleLog(err);
          return "";
        });
    }
    return newShippingAddressId;
  };

  const onSubmit = async () => {
    setIsSubmitting(true);
    await sleep();
    let newShippingAddressId;
    if (isAddingNewShippingAddress) {
      newShippingAddressId = await addNewShippingAddress(values);
    }
    submitCheckoutInfo({
      ...values,
      shippingAddress: {
        ...values.shippingAddress,
        id: isAddingNewShippingAddress
          ? newShippingAddressId
            ? newShippingAddressId
            : ""
          : selectedShippingAddress?.id,
      },
    });
    setIsSubmitting(false);
    history.push("shipping");
  };

  let autocomplete: any;
  let address1Field;

  const initAutocomplete = () => {
    address1Field = document.querySelector("#shippingAddress\\.line1");

    autocomplete = new google.maps.places.Autocomplete(address1Field, {
      componentRestrictions: { country: ["us", "ca"] },
      fields: ["address_components", "geometry"],
      types: ["address"],
    });

    autocomplete.addListener("place_changed", fillInAddress);
  };

  const fillInAddress = () => {
    const place = autocomplete.getPlace();
    let line1 = "";
    let postal_code = "";

    for (const component of place.address_components) {
      const componentType = component.types[0];

      switch (componentType) {
        case "street_number": {
          line1 = `${component.long_name} ${line1}`;
          break;
        }
        case "route": {
          line1 += component.short_name;
          break;
        }
        case "postal_code": {
          postal_code = `${component.long_name}${postal_code}`;
          break;
        }
        case "postal_code_suffix": {
          postal_code = `${postal_code}-${component.long_name}`;
          break;
        }
        case "locality":
          setValue("shippingAddress.city", component.long_name);
          break;
        case "administrative_area_level_1":
          setValue("shippingAddress.state", component.short_name);
          break;
        case "country":
          setValue("shippingAddress.country", component.short_name);
          break;
        default:
          break;
      }
    }
    setValue("shippingAddress.line1", line1);
    setValue("shippingAddress.postal_code", postal_code);
  };

  useEffect(() => {
    if (isAddingNewShippingAddress) {
      initAutocomplete();
    }
  }, [isAddingNewShippingAddress]);

  return (
    <div className={classes.container}>
      <Link to="/" className={classes.logo}>
        LOGO
      </Link>
      <div className={classes.subContainer}>
        <CheckoutLinks checkoutRoutes={checkoutRoutes} />

        <form className={classes.infoFormSection}>
          <fieldset className={classes.expressCheckout}>
            <legend>Express Checkout</legend>
            <div>AliPay</div>
            <div>GooglePay</div>
            <div>PayPal</div>
          </fieldset>
          <fieldset className={classes.horizontalLine}>
            <legend>OR</legend>
          </fieldset>
          <div className={classes.info}>
            <div className={classes.infoHeader}>
              <div className={classes.infoHeaderTitle}>Contact information</div>
              <div className={classes.alreadyHaveAnAccount}>
                <span>Already have an account?</span>
                <Link to="/">Log in</Link>
              </div>
            </div>
            <div className={classes.infoContent}>
              <div className={classes.oneColumn}>
                <TextInput
                  type="email"
                  name="contact.email"
                  label="Email"
                  register={register}
                  errors={errors}
                  values={values}
                />
              </div>
              <label className={classes.keepMeUpToDateLabel}>
                <input
                  type="checkbox"
                  id="contact.keepMeUpToDate"
                  {...register("contact.keepMeUpToDate")}
                  name="contact.keepMeUpToDate"
                />
                <span>
                  <span></span>
                </span>
                Keep me up to date on news and exclusive offers
              </label>
            </div>
          </div>
          <div className={classes.info}>
            <div className={classes.infoHeader}>
              <div className={classes.infoHeaderTitle}>Shipping address</div>
            </div>
            <div className={classes.infoContent}>
              {!isLoading ? (
                <>
                  <div className={classes.oneColumn}>
                    <select
                      onChange={handleChange}
                      name="useSavedAddressOrAddNewAddress"
                      value={selectedShippingAddress?.id}
                      className={classes.select}
                    >
                      {savedShippingAddress.map((sa) => (
                        <option key={sa.id} value={sa.id}>
                          {sa.address.line1 && sa.address.line1}
                          {sa.address.line2 && `, ${sa.address.line2}`}
                          {sa.address.city && `, ${sa.address.city}`}
                          {sa.address.state && `, ${sa.address.state}`}
                          {sa.address.postal_code &&
                            ` ${sa.address.postal_code}`}
                          {sa.address.country && `, ${sa.address.country}`}
                          {sa.name && ` (${sa.name})`}
                        </option>
                      ))}
                      <option value="addNewShippingAddress">
                        ADD A NEW ADDRESS
                      </option>
                    </select>
                    <label className={classes.label}>Saved addresses</label>
                  </div>
                  <div className={classes.twoColumns}>
                    <TextInput
                      name="shippingAddress.firstname"
                      label="First name (optional)"
                      register={register}
                      errors={errors}
                      values={values}
                      disabled={!isAddingNewShippingAddress}
                    />
                    <TextInput
                      name="shippingAddress.lastname"
                      label="Last name"
                      register={register}
                      errors={errors}
                      values={values}
                      required
                      disabled={!isAddingNewShippingAddress}
                    />
                  </div>
                  <div className={classes.oneColumn}>
                    <TextInput
                      name="shippingAddress.company"
                      label="Company (optional)"
                      register={register}
                      errors={errors}
                      values={values}
                      disabled={!isAddingNewShippingAddress}
                    />
                  </div>
                  <div className={classes.oneColumn}>
                    <TextInput
                      name="shippingAddress.line1"
                      label="Enter a location"
                      placeholder=""
                      register={register}
                      errors={errors}
                      values={values}
                      required
                      disabled={!isAddingNewShippingAddress}
                    />
                  </div>
                  <div className={classes.oneColumn}>
                    <TextInput
                      name="shippingAddress.line2"
                      label="Apartment, suite, etc. (optional)"
                      register={register}
                      errors={errors}
                      values={values}
                      disabled={!isAddingNewShippingAddress}
                    />
                  </div>
                  <div className={classes.twoColumns}>
                    <SelectInput
                      name="shippingAddress.country"
                      label="Country"
                      register={register}
                      errors={errors}
                      values={values}
                      disabled={!isAddingNewShippingAddress}
                      required
                    >
                      <option value="" disabled />
                      <option value="CA">Canada</option>
                      <option value="US">United States</option>
                    </SelectInput>
                    <SelectInput
                      name="shippingAddress.state"
                      label="Province"
                      register={register}
                      errors={errors}
                      values={values}
                      disabled={!isAddingNewShippingAddress}
                      required
                    >
                      <option value="" disabled />
                      <option value="AB">Alberta</option>
                      <option value="BC">British Columbia</option>
                      <option value="MB">Manitoba</option>
                      <option value="NB">New Brunswick</option>
                      <option value="NL">Newfoundland and Labrador</option>
                      <option value="NS">Nova Scotia</option>
                      <option value="ON">Ontario</option>
                      <option value="PE">Prince Edward Island</option>
                      <option value="QC">Quebec</option>
                      <option value="SK">Saskatchewan</option>
                    </SelectInput>
                  </div>
                  <div className={classes.twoColumns}>
                    <TextInput
                      name="shippingAddress.city"
                      label="City"
                      register={register}
                      errors={errors}
                      values={values}
                      disabled={!isAddingNewShippingAddress}
                      required
                    />
                    <TextInput
                      name="shippingAddress.postal_code"
                      label="Zip or postal code"
                      register={register}
                      errors={errors}
                      values={values}
                      disabled={!isAddingNewShippingAddress}
                      required
                    />
                  </div>
                  <div className={classes.oneColumn}>
                    <TextInput
                      name="shippingAddress.phone"
                      label="Phone (Recommended)"
                      type="phone"
                      register={register}
                      errors={errors}
                      values={values}
                      setvalue={setValue}
                      disabled={!isAddingNewShippingAddress}
                    />
                  </div>
                </>
              ) : (
                <Loading on={isLoading} />
              )}
            </div>
          </div>
          <div className={classes.btnContainer}>
            <StripeIcon height="0" />
            <button
              type="submit"
              className={classes.continueBtn}
              onClick={handleSubmit(onSubmit)}
            >
              Continue to Shipping
            </button>
          </div>
        </form>
      </div>
      <CheckoutSummary />
      <Loading size="3rem" fullScreen on={isSubmitting || isLoading} />
    </div>
  );
};

export default withAuth(
  GoogleApiWrapper({
    apiKey: `${process.env.REACT_APP_GOOGLE_PLACES_API_KEY}`,
  })(CheckoutInfoPage),
);
